import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { PurchaseRequestService } from '../purchase-request/purchase-request.service';
import { PurchaseReturnOrderService } from '../purchase-return-order/purchase-return-order.service';
import { PurchaseOrderControllerService } from '@app/api/purchase-service/api/purchaseOrderController.service';
import { BlobConverterUtil } from '@shared/utils/blob-converter.util';
import {
  mapPageContentToPaginatedPage,
  PaginatedDropdownLoadParams,
  PaginatedOptionsPage,
} from '@shared/utils/paginated-dropdown.util';
import { from } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ProcurementDropdownService {
  private readonly purchaseRequestService = inject(PurchaseRequestService);
  private readonly porService = inject(PurchaseReturnOrderService);
  private readonly purchaseOrderController = inject(PurchaseOrderControllerService);

  getPurchaseRequestOptionsPage(
    params: PaginatedDropdownLoadParams,
  ): Observable<PaginatedOptionsPage<number>> {
    const searchText = params.searchText?.trim();
    return this.purchaseRequestService
      .getPurchaseRequestRecords({
        page: params.page - 1,
        size: params.size,
        sortBy: 'id',
        ascending: false,
        series: searchText || undefined,
        status: 'APPROVED',
      })
      .pipe(
        map((response) => {
          const pageContent = (response?.pageContent ?? []).filter(
            (pr: { status?: string }) => pr.status === 'APPROVED',
          );
          return mapPageContentToPaginatedPage(
            {
              pageContent,
              totalElements: response?.totalElements,
            },
            (pr) => ({
              label: `${pr.series} - ${pr.title || 'Untitled'}`,
              value: pr.id,
            }),
          );
        }),
        catchError(() => of({ options: [], pageContentLength: 0 })),
      );
  }

  getAvailablePoOptionsPage(
    params: PaginatedDropdownLoadParams,
    filters?: { vendorId?: number; warehouseId?: number },
  ): Observable<PaginatedOptionsPage<number>> {
    return this.porService
      .getAvailablePOs({
        vendorId: filters?.vendorId,
        warehouseId: filters?.warehouseId,
      })
      .pipe(
        map((pos) => {
          const searchText = params.searchText.trim().toLowerCase();
          const filtered = searchText
            ? pos.filter(
                (po) =>
                  po.poNo?.toLowerCase().includes(searchText) ||
                  po.vendorName?.toLowerCase().includes(searchText),
              )
            : pos;
          const start = (params.page - 1) * params.size;
          const pageOptions = filtered.slice(start, start + params.size);
          return {
            options: pageOptions.map((po) => ({
              label: `${po.poNo} - ${po.vendorName || ''}`,
              value: po.id,
            })),
            pageContentLength: pageOptions.length,
            totalElements: filtered.length,
          };
        }),
        catchError(() => of({ options: [], pageContentLength: 0 })),
      );
  }

  getDeliveredPurchaseOrderOptionsPage(
    params: PaginatedDropdownLoadParams,
  ): Observable<PaginatedOptionsPage<number>> {
    const searchText = params.searchText?.trim();
    return this.purchaseOrderController
      .page1(
        undefined,
        undefined,
        searchText || undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        'DELIVERED',
        false,
        params.page - 1,
        params.size,
        'creationDate',
        false,
        'body',
        false,
        { httpHeaderAccept: 'application/json' as any },
      )
      .pipe(
        switchMap((response: unknown) => {
          if (BlobConverterUtil.isBlob(response)) {
            return from(BlobConverterUtil.convertToJson(response));
          }
          return of(response);
        }),
        map((response: Record<string, unknown>) =>
          mapPageContentToPaginatedPage(
            {
              pageContent: BlobConverterUtil.extractPageContent(response, []),
              totalElements: response['totalElements'] as number | undefined,
              hasNextPage: response['hasNextPage'] as boolean | undefined,
            },
            (po: Record<string, unknown>) => ({
              label: String(po['series'] ?? po['id'] ?? ''),
              value: Number(po['id'] ?? 0),
            }),
          ),
        ),
        catchError(() => of({ options: [], pageContentLength: 0 })),
      );
  }
}
